import { QuestionnaireAndAnswers } from '../../../../../types/Questionnaire';
import React, { useEffect } from 'react';
import { useTheme } from '@material-ui/core';
import SectionButton from './SectionButton';
import illustrations from '../../../../illustrations';
import IllustrationProps from '../../../../illustrations/IllustrationProps';
import Icon from '@material-ui/core/Icon';

interface InlineIconProps {
  children?: string;
  variant?: IllustrationProps['variant'];
}

// Map of icon names to available illustrations (for fallback when exact match not found)
const iconToIllustrationMap: Record<string, keyof typeof illustrations> = {
  // Common icon mappings
  warning: 'assesments',
  error: 'assesments',
  medical_services: 'medications',
  local_pharmacy: 'medications',
  healing: 'medications',
  check_circle: 'checkin',
  assignment_turned_in: 'checkin',
  assignment: 'assesments',
  description: 'nurse',
  note: 'nurse',
  create: 'nurse',
  favorite: 'diagnostic',
  science: 'diagnostic',
  opacity: 'diagnostic',
};

const InlineIcon = ({ children, variant }: InlineIconProps) => {
  const theme: any = useTheme();

  // Log missing icons in development for debugging
  useEffect(() => {
    if (children && !illustrations[children] && !theme?.illustrations?.[children]) {
      console.debug(
        `[InlineIcon] Icon "${children}" not found in illustrations. Available: ${Object.keys(illustrations).join(', ')}`
      );
    }
  }, [children, theme]);

  if (!children) {
    return null;
  }

  // First try direct illustration match
  const Illustration = illustrations[children];
  if (Illustration) {
    return <Illustration width={56} height={56} variant={variant} />;
  }

  // Try theme illustrations
  const themeIcon = theme?.illustrations?.[children];
  if (themeIcon) {
    return <img alt={children} width={56} height={56} src={themeIcon} style={{ pointerEvents: 'none' }} />;
  }

  // Try mapped illustration fallback
  const mappedIllustration = iconToIllustrationMap[children];
  if (mappedIllustration && illustrations[mappedIllustration]) {
    const MappedIllustration = illustrations[mappedIllustration];
    return <MappedIllustration width={56} height={56} variant={variant} />;
  }

  // Final fallback: Material UI Icon (gracefully handles unknown icons)
  return (
    <Icon style={{ fontSize: 48, opacity: 0.6 }} color={variant === 'light' ? 'secondary' : 'primary'}>
      {children}
    </Icon>
  );
};

const FormButtonDialog = ({
  form,
  handleClickOpen,
}: {
  form: QuestionnaireAndAnswers;
  handleClickOpen: (form: QuestionnaireAndAnswers) => void;
}) => (
  <SectionButton
    onClick={() => handleClickOpen(form)}
    Icon={<InlineIcon variant={form?.completed ? 'light' : 'dark'}>{form?.questionnaire?.icon}</InlineIcon>}
    text={form?.questionnaire?.name || 'Patient Form'}
    status={form?.completed}
  />
);

export default FormButtonDialog;
